import { useState } from "react";
import { ArrowLeft, CheckCircle, Wrench, WarningCircle, Warning, Ruler, Barcode, PaperPlaneTilt } from "@phosphor-icons/react";
import { photoUrl, roomLabel, roomPhotos } from "./walkApi";
import FieldRadarLogo from "./FieldRadarLogo";

// Findings review before completing — grouped by item (Blinds, Windows…), each can become a WO.
export default function ReviewScreen({ data, staff, onBack, onComplete, onFindingWo, onSendOwner }) {
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(null);
  const rooms = data.rooms || [];
  const findings = [];
  rooms.forEach((r, ri) => {
    (r.items || []).forEach(it => {
      if (it.rating === "fair" || it.rating === "dmg") {
        const thumb = roomPhotos(r).find(p => p.item === it.name) || roomPhotos(r)[0];
        findings.push({ roomIdx: ri, room: roomLabel(r), item: it.name, rating: it.rating, note: it.note, wo_id: it.wo_id,
          size: r.measurements?.[it.name], thumb, appliance: r.appliances?.[it.name.replace(/[./]/g, "_")] });
      }
    });
    if (r.fast_flag) findings.push({ roomIdx: ri, room: roomLabel(r), item: "Room flag", rating: "dmg", note: r.fast_flag, wo_id: r.fast_flag_wo_id, thumb: roomPhotos(r)[0] });
  });
  const groups = findings.reduce((m, f) => { (m[f.item] = m[f.item] || []).push(f); return m; }, {});
  const dmg = findings.filter(f => f.rating === "dmg").length;
  const notDone = rooms.filter(r => r.status !== "done").length;

  const makeWo = async (f) => {
    setBusy(`${f.roomIdx}-${f.item}`);
    try { await onFindingWo(f.roomIdx, f.item === "Room flag" ? null : f.item); } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen bg-[#0b1c2d] text-white pb-32" data-testid="walk-review">
      <div className="sticky top-0 z-20 bg-[#0b1c2d]/95 backdrop-blur px-4 pt-3 pb-3 flex items-center gap-2 border-b border-white/10">
        <button onClick={onBack} className="w-11 h-11 -ml-2 flex items-center justify-center rounded-full active:bg-white/10" data-testid="review-back"><ArrowLeft weight="bold" size={26} className="text-teal-400"/></button>
        <div className="flex-1 min-w-0">
          <FieldRadarLogo size={20} sub="Review findings"/>
        </div>
      </div>
      <div className="px-4 pt-4">
        <div className="font-black text-2xl">{findings.length} finding{findings.length === 1 ? "" : "s"}</div>
        <div className="text-sm text-slate-300 flex gap-3 mt-1">
          <span className="text-red-300 font-bold">{dmg} damage</span><span className="text-yellow-300 font-bold">{findings.length - dmg} fair</span>
          {notDone > 0 && <span className="text-slate-400">· {notDone} room{notDone === 1 ? "" : "s"} not marked done</span>}
        </div>
        {findings.length === 0 && <div className="mt-6 p-4 rounded-2xl bg-emerald-500/10 border border-emerald-400/30 text-emerald-200 font-bold">Nothing flagged — unit looks clean.</div>}
        <div className="mt-4 space-y-5">
          {Object.entries(groups).map(([item, fs]) => (
            <div key={item} data-testid={`review-group-${item.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
              <div className="text-[11px] font-black uppercase tracking-widest text-teal-400 mb-2">{item} · {fs.length}</div>
              <div className="space-y-2">
                {fs.map(f => (
                  <div key={`${f.roomIdx}-${f.item}`} className="flex gap-3 p-3 rounded-2xl bg-[#13263a] border border-white/10">
                    <div className="w-16 h-16 rounded-xl overflow-hidden bg-black/30 shrink-0">
                      {f.thumb && <img src={photoUrl(f.thumb)} alt="" className="w-full h-full object-cover"/>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold truncate">{f.room}</span>
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-black flex items-center gap-0.5 ${f.rating === "dmg" ? "bg-red-600" : "bg-yellow-400 text-slate-900"}`}>
                          {f.rating === "dmg" ? <Warning weight="fill" size={11}/> : <WarningCircle weight="fill" size={11}/>} {f.rating === "dmg" ? "Damage" : "Fair"}
                        </span>
                      </div>
                      {f.note && <div className="text-xs text-slate-300 mt-0.5 line-clamp-2">{f.note}</div>}
                      {f.size && <div className="text-xs text-teal-300 mt-0.5 flex items-center gap-1"><Ruler size={12}/> {f.size.width_in ?? "?"}″ × {f.size.height_in ?? "?"}″</div>}
                      {f.appliance && <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-1"><Barcode size={12}/> {f.appliance.brand} {f.appliance.model}</div>}
                      {staff && (
                        f.wo_id ? <div className="mt-1.5 text-[11px] font-black text-teal-300 uppercase">Work order created</div>
                        : <button onClick={() => makeWo(f)} disabled={busy === `${f.roomIdx}-${f.item}`} data-testid={`review-wo-${f.roomIdx}-${f.item.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                            className="mt-1.5 h-9 px-3 rounded-lg bg-white/10 text-xs font-black flex items-center gap-1.5 disabled:opacity-50"><Wrench weight="fill" size={14}/> Create work order</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="fixed bottom-0 inset-x-0 p-4 bg-gradient-to-t from-[#0b1c2d] via-[#0b1c2d] to-transparent space-y-2">
        {onSendOwner && findings.length > 0 && (
          <button onClick={async () => { setSending(true); try { await onSendOwner(); } catch (e) { /* toast in caller */ } finally { setSending(false); } }}
            disabled={sending} data-testid="review-send-owner"
            className="w-full h-12 rounded-2xl bg-amber-400 text-slate-900 font-black text-base flex items-center justify-center gap-2 disabled:opacity-60">
            <PaperPlaneTilt weight="fill" size={20}/> {sending ? "Sending…" : `Send ${findings.length} findings to owner for approval`}
            {data.findings_approval_status && <span className="text-[10px] uppercase tracking-widest bg-slate-900 text-amber-300 px-2 py-0.5 rounded">{data.findings_approval_status.replace("_", " ")}</span>}
          </button>
        )}
        <button onClick={onComplete} data-testid="review-complete"
          className="w-full h-14 rounded-2xl bg-teal-500 text-slate-900 font-black text-lg flex items-center justify-center gap-2 shadow-lg shadow-teal-500/20">
          <CheckCircle weight="fill" size={24}/> {staff ? "Complete inspection" : "Send to office"}
        </button>
      </div>
    </div>
  );
}
